'use client'

import {
  Globe,
  Lock,
  PanelLeft,
  PanelLeftDashed,
  PanelRight,
  PanelRightDashed,
  Share2} from 'lucide-react'
import { usePathname } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'

import { getChatPublicStatus } from '@/api/agent/agentApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { useAgentHistoryPanel } from '../contexts/AgentHistoryPanelContext'
import { ShareDialog } from './ShareDialog'

/** Current chat id parsed from pathname when on agent chat routes. */
function useAgentChatIdFromPath(): string | undefined {
  const pathname = usePathname()
  const p = String(pathname || '')
  const match = p.match(/^\/agent\/chats\/([^/]+)$|^\/(?:v2\/)?agent-ide\/session\/([^/]+)$/)
  return match?.[1] || match?.[2]
}

export function AgentTopNavRight() {
  const pathname = usePathname()
  const currentChatId = useAgentChatIdFromPath()
  const {
    isAgentsPanelOpen,
    setIsAgentsPanelOpen,
    isFilesPanelOpen,
    toggleFilesPanel
  } = useAgentHistoryPanel()
  const isV2AgentIdeSessionRoute = /^\/v2\/agent-ide\/session\/[^/]+$/.test(String(pathname || ''))
  const isAgentIdeSessionRoute = /^\/(?:v2\/)?agent-ide\/session\/[^/]+$/.test(String(pathname || ''))
  const shouldShowShareControls = Boolean(currentChatId) && !isV2AgentIdeSessionRoute
  const [chatPublicStatus, setChatPublicStatus] = useState<{ id: string; isPublic: boolean } | null>(null)
  const [loadingPublicStatus, setLoadingPublicStatus] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const lastFetchedChatIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!shouldShowShareControls || !currentChatId) {
      setChatPublicStatus(null)
      setLoadingPublicStatus(false)
      setShowShareDialog(false)
      lastFetchedChatIdRef.current = null
      return
    }
    if (lastFetchedChatIdRef.current === currentChatId) return

    let cancelled = false
    const run = async () => {
      try {
        setLoadingPublicStatus(true)
        const status = await getChatPublicStatus(currentChatId)
        if (!cancelled) {
          setChatPublicStatus(status)
          lastFetchedChatIdRef.current = currentChatId
        }
      } catch {
        if (!cancelled) {
          setChatPublicStatus(null)
          lastFetchedChatIdRef.current = null
        }
      } finally {
        if (!cancelled) setLoadingPublicStatus(false)
      }
    }
    const t = setTimeout(run, 100)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [currentChatId, shouldShowShareControls])

  return (
    <>
      <div className="flex items-center gap-1 sm:gap-2">
        {shouldShowShareControls && (
          <>
            {loadingPublicStatus ? (
              <Badge variant="outline" className="h-7 px-1.5 sm:px-2 text-xs">
                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                <span className="hidden sm:inline">Loading...</span>
                <span className="sm:hidden">...</span>
              </Badge>
            ) : chatPublicStatus ? (
              <Badge
                variant={chatPublicStatus.isPublic ? 'default' : 'secondary'}
                className="h-7 px-1.5 sm:px-2 text-xs flex items-center gap-1 flex-shrink-0"
              >
                {chatPublicStatus.isPublic ? (
                  <>
                    <Globe className="h-3 w-3 flex-shrink-0" />
                    <span className="hidden sm:inline">Public</span>
                  </>
                ) : (
                  <>
                    <Lock className="h-3 w-3 flex-shrink-0" />
                    <span className="hidden sm:inline">Private</span>
                    <span className="sm:hidden">Priv</span>
                  </>
                )}
              </Badge>
            ) : null}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowShareDialog(true)}
              className="h-8 w-8"
              title="Share chat"
              aria-label="Share chat"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </>
        )}

        {isAgentIdeSessionRoute ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleFilesPanel}
            className="h-8 w-8"
            title={isFilesPanelOpen ? 'Close files panel' : 'Open files panel'}
            aria-label={isFilesPanelOpen ? 'Close files panel' : 'Open files panel'}
          >
            {isFilesPanelOpen ? <PanelLeft className="h-5 w-5" /> : <PanelLeftDashed className="h-5 w-5" />}
          </Button>
        ) : null}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setIsAgentsPanelOpen(!isAgentsPanelOpen)}
          className="h-8 w-8"
          title={isAgentsPanelOpen ? 'Close chat history' : 'Open chat history'}
          aria-label={isAgentsPanelOpen ? 'Close chat history' : 'Open chat history'}
        >
          {isAgentsPanelOpen ? <PanelRight className="h-5 w-5" /> : <PanelRightDashed className="h-5 w-5" />}
        </Button>
      </div>

      {showShareDialog && shouldShowShareControls && currentChatId && (
        <ShareDialog
          chatId={currentChatId}
          isPublic={chatPublicStatus?.isPublic ?? false}
          onClose={() => setShowShareDialog(false)}
          onPublicStatusChange={(newStatus: boolean) => {
            setChatPublicStatus((prev) => (prev ? { ...prev, isPublic: newStatus } : null))
          }}
        />
      )}
    </>
  )
}
